import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { setAxiosStore } from './apiSlices/axiosBaseQuery';

// Import API slices
import {
  companiesCustomerMiddleware,
  companiesCustomerReducer,
  companiesCustomerReducerPath,
} from './apiSlices/companies/companiesCustomer/companiesCustomerApis';
import {
  companiesMakersMiddleware,
  companiesMakersReducer,
  companiesMakersReducerPath,
} from './apiSlices/companies/companiesMakers/companiesMakersApis';
import {
  companiesSuppliersMiddleware,
  companiesSuppliersReducer,
  companiesSuppliersReducerPath,
} from './apiSlices/companies/companiesSuppliers/companiesSuppliersApis';
import {
  deliveryMiddleware,
  deliveryReducer,
  deliveryReducerPath,
} from './apiSlices/delivery/deliveryApis';
import {
  extraCostsMiddleware,
  extraCostsReducer,
  extraCostsReducerPath,
} from './apiSlices/extracosts/extraCostsApis';
import {
  lubricantCategoriesMiddleware,
  lubricantCategoriesReducer,
  lubricantCategoriesReducerPath,
} from './apiSlices/lubricants/lubricantCategory/lubricantCategoryApis';
import {
  lubricantGroupsMiddleware,
  lubricantGroupsReducer,
  lubricantGroupsReducerPath,
} from './apiSlices/lubricants/lubricantGroups/lubricantGroupsApis';
import {
  lubricantProductsMiddleware,
  lubricantProductsReducer,
  lubricantProductsReducerPath,
} from './apiSlices/lubricants/lubricantProducts/lubricantProductsApis';
import {
  lubricantTypesMiddleware,
  lubricantTypesReducer,
  lubricantTypesReducerPath,
} from './apiSlices/lubricants/lubricantTypes/lubricantTypesApis';
import {
  managementPeriodMiddleware,
  managementPeriodReducer,
  managementPeriodReducerPath,
} from './apiSlices/managementperiods/managementPeriodsApis';
import { portsMiddleware, portsReducer, portsReducerPath } from './apiSlices/ports/portsApis';
import { robsMiddleware, robsReducer, robsReducerPath } from './apiSlices/robs/robsApis';
import { userMiddleware, userReducer, userReducerPath } from './apiSlices/userApiSlice';

// Import normal slices
import {
  LubricationChartsMiddleware,
  LubricationChartsReducer,
  LubricationChartsReducerPath,
} from './apiSlices/lubricationChart/lubricationChartsApis';
import {
  priceListMiddleware,
  priceListReducer,
  priceListReducerPath,
} from './apiSlices/priceLists/priceListApis';
import {
  typeInProductsMiddleware,
  typeInProductsReducer,
  typeInProductsReducerPath,
} from './apiSlices/typeInProducts/typeInProductsApis';
import persistedUserReducer from './slices/userSlice';

// Combine reducers
const rootReducer = combineReducers({
  user: persistedUserReducer,
  [userReducerPath]: userReducer,
  [deliveryReducerPath]: deliveryReducer,
  [lubricantProductsReducerPath]: lubricantProductsReducer,
  [lubricantCategoriesReducerPath]: lubricantCategoriesReducer,
  [lubricantTypesReducerPath]: lubricantTypesReducer,
  [lubricantGroupsReducerPath]: lubricantGroupsReducer,
  [extraCostsReducerPath]: extraCostsReducer,
  [managementPeriodReducerPath]: managementPeriodReducer,
  [robsReducerPath]: robsReducer,
  [companiesMakersReducerPath]: companiesMakersReducer,
  [companiesSuppliersReducerPath]: companiesSuppliersReducer,
  [companiesCustomerReducerPath]: companiesCustomerReducer,
  [portsReducerPath]: portsReducer,
  [LubricationChartsReducerPath]: LubricationChartsReducer,
  [priceListReducerPath]: priceListReducer,
  [typeInProductsReducerPath]: typeInProductsReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

const persistConfig = {
  key: 'root',
  storage,
  version: 1,
  blacklist: [],
};

const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware: any) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat([
      userMiddleware,
      deliveryMiddleware,
      lubricantProductsMiddleware,
      lubricantCategoriesMiddleware,
      lubricantTypesMiddleware,
      lubricantGroupsMiddleware,
      extraCostsMiddleware,
      managementPeriodMiddleware,
      robsMiddleware,
      companiesMakersMiddleware,
      companiesSuppliersMiddleware,
      companiesCustomerMiddleware,
      portsMiddleware,
      LubricationChartsMiddleware,
      priceListMiddleware,
      typeInProductsMiddleware,
    ]),
});

setAxiosStore(store);

export const persistor = persistStore(store);

// Infer the `AppDispatch` type from the store itself
export type AppDispatch = typeof store.dispatch;
